import type { Product, Service } from "../types";
import { catalogApi } from "../api/catalog";
import { catalogService, mapProductSummaryToProduct } from "./catalogService";
import { companiesService } from "./companiesService";
import { eventPackService } from "./eventPackService";

export const productsService = {
  async getByCompany(eventId: string, companyId: string): Promise<Product[]> {
    return catalogService.listProducts(eventId, companyId);
  },

  async getById(eventId: string, companyId: string, id: string): Promise<Product | undefined> {
    const products = await this.getByCompany(eventId, companyId);
    return products.find((p) => p.id === id);
  },

  async getByEvent(eventId: string): Promise<Product[]> {
    const fromPack = await eventPackService.listProducts(eventId);
    if (fromPack.length) return fromPack.map(mapProductSummaryToProduct);
    // Pack present but empty → real empty catalog (do NOT N+1 every company).
    const pack = await eventPackService.getCached(eventId);
    if (pack) return [];
    // No pack yet: limited live sample, not full fan-out.
    const companies = await companiesService.list(eventId);
    const sample = companies.slice(0, 24);
    const lists = await Promise.all(
      sample.map((c) => catalogService.listProducts(eventId, c.id).catch(() => [] as Product[])),
    );
    return lists.flat();
  },

  async search(eventId: string, query: string): Promise<Product[]> {
    const result = await catalogService.search(eventId, query);
    return result?.products ?? [];
  },

  /** Random sample of products across exhibitors (home block). */
  async getFeatured(eventId: string, limit = 4): Promise<Product[]> {
    const fromPack = await eventPackService.listProducts(eventId);
    if (fromPack.length) {
      const shuffled = [...fromPack].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, limit).map(mapProductSummaryToProduct);
    }

    const companies = await companiesService.list(eventId);
    if (!companies.length) return [];

    const shuffledCompanies = [...companies].sort(() => Math.random() - 0.5);
    const sample = shuffledCompanies.slice(0, Math.min(12, shuffledCompanies.length));
    const lists = await Promise.all(
      sample.map((c) => this.getByCompany(eventId, c.id).catch(() => [] as Product[])),
    );
    const pool = lists.flat().filter((p) => p?.id);
    if (!pool.length) return [];

    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, limit);
  },

  async getFavorites(eventId: string): Promise<Product[]> {
    const data = await catalogApi.listFavoriteProducts(eventId);
    return data.products;
  },

  async isFavorite(eventId: string, productId: string): Promise<boolean> {
    const data = await catalogApi.isProductFavorite(eventId, productId);
    return data.favorited;
  },

  async addFavorite(eventId: string, productId: string): Promise<void> {
    await catalogApi.addProductFavorite(eventId, productId);
  },

  async removeFavorite(eventId: string, productId: string): Promise<void> {
    await catalogApi.removeProductFavorite(eventId, productId);
  },
};

export const servicesService = {
  async getByCompany(eventId: string, companyId: string): Promise<Service[]> {
    return catalogService.listServices(eventId, companyId);
  },

  async getByEvent(eventId: string): Promise<Service[]> {
    const companies = await companiesService.list(eventId);
    const sample = companies.slice(0, 24);
    const lists = await Promise.all(
      sample.map((c) => catalogService.listServices(eventId, c.id).catch(() => [] as Service[])),
    );
    return lists.flat();
  },

  async search(eventId: string, query: string): Promise<Service[]> {
    const result = await catalogService.search(eventId, query);
    return result?.services ?? [];
  },
};
